<template>
  <div class="create-event-page container-fluid p-0">
    <nav class="steps-navigation">
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: '100%' }"></div>
      </div>

      <div class="steps-row">
        <div class="step-dot-item active">
          <div class="step-dot"><i class="fas fa-envelope-open-text"></i></div>
          <span class="step-dot-label">Ativação por e-mail</span>
        </div>
      </div>
    </nav>

    <main class="step-content-wrapper">
      <div class="container-fluid">
        <div class="step-content py-4 p-0 pt-1 p-md-4">
          <h3 class="step-title">Ative seu cadastro</h3>
          <p class="step-description">
            Enviamos um código para seu e-mail. Digite abaixo para concluir a ativação.
          </p>
          <p v-if="maskedActivationEmail" class="step-email-hint">
            Código enviado para <strong>{{ maskedActivationEmail }}</strong>
          </p>

          <div class="activation-card">
            <div class="field-group mb-3">
              <label class="field-label" for="activation-code">Código de ativação</label>
              <div class="code-input-group">
                <input
                  id="activation-code"
                  ref="codeInputRef"
                  v-model="code"
                  type="text"
                  class="form-control register-input register-input--with-action"
                  placeholder="Digite ou cole o código"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  :disabled="isVerifying"
                  @paste="handlePaste"
                />

                <button
                  type="button"
                  class="btn btn-resend-inline"
                  :disabled="isVerifying || isResending || resendCountdown > 0"
                  @click="sendActivationCode()"
                >
                  <span
                    v-if="isResending"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {{ resendButtonLabel }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <BottomModal
      :visible="feedbackModal.visible"
      @close="closeFeedbackModal"
      :fullHeight="false"
    >
      <template #default>
        <div class="p-3 text-center">
          <h5 class="mb-2 text-primary-emphasis">{{ feedbackModal.title }}</h5>
          <p class="text-muted mb-4">{{ feedbackModal.description }}</p>
          <button class="btn btn-color-site w-100" @click="handleFeedbackPrimaryAction">
            {{ feedbackModal.primaryLabel }}
          </button>
        </div>
      </template>
    </BottomModal>

    <footer class="app-footer">
      <div class="app-footer__inner">
        <p class="app-footer__step-hint">
          Verifique seu e-mail para ativar sua conta
        </p>

        <div class="app-footer__actions">
          <button
            class="btn btn-footer-back"
            @click="skipForNow"
            :disabled="isLoading"
          >
            <i class="fas fa-chevron-left me-1"></i> Pular por enquanto
          </button>

          <button
            class="btn btn-footer-finish"
            @click="activateAccount"
            :disabled="isLoading"
          >
            <span v-if="loadingAction === 'verify'" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            {{ loadingAction === 'verify' ? 'Ativando...' : 'Ativar cadastro' }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { parseError } from '@/utils/errorHandler';
import { accountActivationService } from '@/services/accountActivationService';
import BottomModal from '@/components/BottomModal.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const code = ref('');
const codeInputRef = ref(null);
const resendCountdown = ref(0);
const cooldownInterval = ref(null);
const loadingAction = ref(null);
const feedbackModalAction = ref(null);
const feedbackModal = ref({
  visible: false,
  title: '',
  description: '',
  primaryLabel: 'Entendi',
});

const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

const normalizedCode = computed(() => code.value.replace(/\s+/g, '').trim());
const isLoading = computed(() => loadingAction.value !== null);
const isVerifying = computed(() => loadingAction.value === 'verify');
const isResending = computed(() => loadingAction.value === 'resend');
const resendButtonLabel = computed(() => {
  if (isResending.value) return 'Enviando...';
  if (resendCountdown.value > 0) return `Reenviar (${resendCountdown.value}s)`;
  return 'Reenviar';
});

const targetAfterSkip = computed(() => {
  if (typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/')) {
    return route.query.redirect;
  }
  return '/my-account/dashboard';
});

const activationEmail = computed(() => {
  if (typeof route.query.email === 'string' && route.query.email.trim()) {
    return route.query.email.trim();
  }

  if (authStore.user?.email) {
    return authStore.user.email;
  }

  const rawRegister = sessionStorage.getItem('register_data');
  if (!rawRegister) return '';

  try {
    const parsed = JSON.parse(rawRegister);
    return parsed?.email?.trim() || '';
  } catch {
    return '';
  }
});

const maskedActivationEmail = computed(() => {
  const rawEmail = activationEmail.value;
  if (!rawEmail || !rawEmail.includes('@')) return '';

  const [localPart, domainPart = ''] = rawEmail.split('@');
  const visibleLocal = localPart.slice(0, 2);

  if (!visibleLocal || !domainPart) return '';
  return `${visibleLocal}***@${domainPart}`;
});

const openFeedbackModal = ({ title, description, primaryLabel = 'Entendi', onPrimary = null }) => {
  feedbackModal.value = {
    visible: true,
    title,
    description,
    primaryLabel,
  };
  feedbackModalAction.value = onPrimary;
};

const closeFeedbackModal = () => {
  feedbackModal.value.visible = false;
  feedbackModalAction.value = null;
};

const handleFeedbackPrimaryAction = () => {
  const onPrimary = feedbackModalAction.value;
  closeFeedbackModal();
  if (typeof onPrimary === 'function') {
    onPrimary();
  }
};

const clearCooldownInterval = () => {
  if (cooldownInterval.value) {
    clearInterval(cooldownInterval.value);
    cooldownInterval.value = null;
  }
};

const getRemainingResendSeconds = () => {
  const nextResendAt = authStore.activationNextResendAt;
  if (!nextResendAt) return 0;

  const remainingMs = nextResendAt - Date.now();
  if (remainingMs <= 0) return 0;

  return Math.ceil(remainingMs / 1000);
};

const syncResendCountdown = () => {
  resendCountdown.value = getRemainingResendSeconds();
  if (resendCountdown.value <= 0) {
    clearCooldownInterval();
  }
};

const startCooldown = (nextResendAt) => {
  authStore.setActivationNextResendAt(nextResendAt);
  syncResendCountdown();

  if (resendCountdown.value <= 0) {
    return;
  }

  clearCooldownInterval();

  cooldownInterval.value = setInterval(() => {
    syncResendCountdown();
  }, 1000);
};

const extractResendCooldownSeconds = (response) => {
  const payload = response?.data ?? {};
  const headers = response?.headers ?? {};

  const secondsCandidates = [
    payload.cooldown_seconds,
    payload.cooldownSeconds,
    payload.retry_after,
    payload.retryAfter,
    payload.resend_cooldown_seconds,
    payload.resendCooldownSeconds,
    headers['retry-after'],
    headers['Retry-After'],
  ];

  for (const rawValue of secondsCandidates) {
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return Math.ceil(numericValue);
    }
  }

  const nextResendCandidates = [
    payload.next_resend_at,
    payload.nextResendAt,
    payload.next_allowed_resend_at,
    payload.nextAllowedResendAt,
  ];

  for (const rawValue of nextResendCandidates) {
    if (rawValue === null || rawValue === undefined || rawValue === '') continue;

    let targetTimestamp = null;

    if (typeof rawValue === 'number') {
      targetTimestamp = rawValue > 1e12 ? rawValue : rawValue * 1000;
    } else {
      const parsedDate = Date.parse(String(rawValue));
      if (!Number.isNaN(parsedDate)) {
        targetTimestamp = parsedDate;
      }
    }

    if (targetTimestamp && Number.isFinite(targetTimestamp) && targetTimestamp > Date.now()) {
      return Math.ceil((targetTimestamp - Date.now()) / 1000);
    }
  }

  return DEFAULT_RESEND_COOLDOWN_SECONDS;
};

const buildActivationError = (error, fallbackMessage) => {
  if (!error?.response) {
    return 'Sem conexão com a internet. Verifique sua rede e tente novamente.';
  }

  const backendMessage = parseError(error) || fallbackMessage;
  const normalizedMessage = backendMessage.toLowerCase();

  if (normalizedMessage.includes('expir')) {
    return 'Código expirado. Solicite um novo código para continuar.';
  }

  if (normalizedMessage.includes('invál') || normalizedMessage.includes('invalid')) {
    return 'Código inválido. Confira os dígitos e tente novamente.';
  }

  return backendMessage;
};

const validateCode = () => {
  if (!normalizedCode.value) {
    return 'Informe o código de ativação.';
  }

  if (!/^[a-zA-Z0-9]{4,8}$/.test(normalizedCode.value)) {
    return 'Use um código válido com 4 a 8 caracteres.';
  }

  return null;
};

const sendActivationCode = async ({ silentSuccess = false } = {}) => {
  if (resendCountdown.value > 0 || isLoading.value) return;

  loadingAction.value = 'resend';

  try {
    const result = await accountActivationService.resendCode({ email: activationEmail.value });
    const cooldownSeconds = extractResendCooldownSeconds(result.response);
    const nextResendAt = Date.now() + (cooldownSeconds * 1000);

    startCooldown(nextResendAt);
    code.value = '';

    await nextTick();
    codeInputRef.value?.focus?.();

    if (!silentSuccess) {
      openFeedbackModal({
        title: 'Código reenviado',
        description: maskedActivationEmail.value
          ? `Enviamos um novo código para ${maskedActivationEmail.value}.`
          : 'Enviamos um novo código para o seu e-mail.',
      });
    }
  } catch (error) {
    const status = error?.response?.status;
    if (status === 429) {
      const retryAfterSeconds = extractResendCooldownSeconds(error.response);
      const nextResendAt = Date.now() + (retryAfterSeconds * 1000);
      startCooldown(nextResendAt);
    }

    const friendlyMessage = buildActivationError(error, 'Não foi possível reenviar o código agora.');
    openFeedbackModal({
      title: 'Não foi possível reenviar',
      description: friendlyMessage,
      primaryLabel: 'Tentar novamente',
    });
  } finally {
    loadingAction.value = null;
  }
};

const activateAccount = async () => {
  if (isLoading.value) return;

  const validationMessage = validateCode();
  if (validationMessage) {
    openFeedbackModal({
      title: 'Código inválido',
      description: validationMessage,
    });
    return;
  }

  loadingAction.value = 'verify';

  try {
    await accountActivationService.verifyCode({
      code: normalizedCode.value,
      email: activationEmail.value,
    });

    authStore.applyBackendEligibilityFlags({
      isActivated: true,
      emailVerified: true,
    });
    await authStore.fetchUserProfile();
    authStore.clearActivationResendCooldown();

    openFeedbackModal({
      title: 'Cadastro ativado',
      description: 'Tudo certo! Seu cadastro foi ativado com sucesso.',
      primaryLabel: 'Continuar',
      onPrimary: () => router.push('/my-account/dashboard'),
    });
  } catch (error) {
    const friendlyMessage = buildActivationError(error, 'Não foi possível ativar seu cadastro agora.');
    openFeedbackModal({
      title: 'Não foi possível ativar',
      description: friendlyMessage,
      primaryLabel: 'Tentar novamente',
    });
  } finally {
    loadingAction.value = null;
  }
};

const skipForNow = () => {
  if (isLoading.value) return;
  router.push(targetAfterSkip.value);
};

const handlePaste = (event) => {
  event.preventDefault();
  const pasted = event.clipboardData.getData('text') || '';
  code.value = pasted.replace(/\s+/g, '').trim();
};

onMounted(() => {
  if (authStore.activationStatusLoaded && authStore.isActivated) {
    router.replace(targetAfterSkip.value);
    return;
  }

  const hasStoredCooldown = authStore.activationNextResendAt !== null;
  const isCooldownActive = getRemainingResendSeconds() > 0;

  if (isCooldownActive) {
    startCooldown(authStore.activationNextResendAt);
    return;
  }

  if (!hasStoredCooldown) {
    sendActivationCode({ silentSuccess: true });
  }
});

onBeforeUnmount(() => {
  clearCooldownInterval();
});
</script>

<style scoped>
.create-event-page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  height: 100%;
  width: 100%;
  min-width: 600px;
  overflow-x: clip;
  box-sizing: border-box;
}

.steps-navigation {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 90;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem 1.5rem 0.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  margin-top: 0;
}

.progress-track {
  position: relative;
  height: 3px;
  background-color: #e9ecef;
  border-radius: 99px;
  margin: 0 auto 1rem;
  max-width: 320px;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #ff50a3, #ff8cc8);
  border-radius: 99px;
}

.steps-row {
  display: flex;
  justify-content: center;
  max-width: 360px;
  margin: 0 auto;
  gap: 8px;
}

.step-dot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  opacity: 1;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #ff50a3;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(255, 80, 163, 0.18);
}

.step-dot-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #ff50a3;
  white-space: nowrap;
}

.step-content-wrapper {
  flex-grow: 1;
  padding: 0px !important;
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  overflow-x: clip;
  box-sizing: border-box;
}

.step-content-wrapper > .container-fluid {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  overflow-x: clip;
  box-sizing: border-box;
}

.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.25rem;
}

.step-description {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0.15rem;
}

.step-email-hint {
  font-size: 0.86rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.step-email-hint strong {
  color: #32355d;
}

.activation-card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
}

.field-group {
  margin-bottom: 0 !important;
}

.field-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.register-input {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.register-input:focus {
  background-color: #fff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  outline: none;
}

.register-input::placeholder {
  color: #c0c0c0;
  font-size: 0.88rem;
}

.code-input-group {
  position: relative;
}

.register-input--with-action {
  padding-right: 7.5rem;
}

.btn-resend-inline {
  position: absolute;
  right: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  background: #fff0f6;
  color: #ff50a3;
  border: 1px solid #ffc1de;
  border-radius: 0.65rem;
  padding: 0.24rem 0.58rem;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.1;
  min-width: 96px;
}

.btn-resend-inline:hover:not(:disabled) {
  background: #ffe2f0;
  color: #e64292;
}

.btn-resend-inline:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.text-link {
  color: #ff50a3;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
}

.text-link:hover {
  text-decoration: underline;
}

.app-footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  pointer-events: none;
}

.app-footer__inner {
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -4px 16px rgba(0,0,0,0.07);
  padding: 0.75rem 1.25rem 1rem;
  pointer-events: all;
  max-width: 640px;
  margin: 0 auto;
  border-radius: 16px 16px 0 0;
}

.app-footer__step-hint {
  font-size: 0.72rem;
  color: #b0b0b0;
  text-align: center;
  margin-bottom: 0.6rem;
}

.app-footer__actions {
  display: flex;
  gap: 10px;
}

.btn-footer-back {
  background: #fff;
  border: 1.5px solid #e0e0e0;
  color: #6c757d;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 600;
}

.btn-footer-finish {
  flex: 1;
  background: #ff50a3 !important;
  background-image: linear-gradient(135deg, #ff50a3, #d63384) !important;
  color: #fff !important;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.35);
}

.btn-color-site {
  background: #ff50a3 !important;
  background-image: linear-gradient(135deg, #ff50a3, #d63384) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 0.75rem;
  font-weight: 700;
  padding: 0.65rem 1.25rem;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.35);
}

.btn-color-site:hover,
.btn-color-site:focus,
.btn-color-site:active,
.btn-color-site:focus-visible {
  background: #e8417f !important;
  background-image: linear-gradient(135deg, #e8417f, #c0296e) !important;
  color: #fff !important;
  outline: none;
}

@media (max-width: 767.98px) {
  .create-event-page {
    transform: translateZ(0);
    min-width: 0;
  }

  .steps-navigation {
    position: -webkit-sticky;
    position: sticky;
    top: -1rem;
    z-index: 200;
    margin-top: -1rem;
    padding: 0.75rem 1rem 0.65rem;
  }

  .step-dot {
    width: 26px;
    height: 26px;
  }

  .step-dot-label {
    font-size: 0.68rem;
  }

  .step-content-wrapper {
    padding: 1.5rem 1rem 5rem !important;
  }

  .register-input--with-action {
    padding-right: 7.1rem;
  }

  .btn-resend-inline {
    min-width: 90px;
    padding: 0.22rem 0.5rem;
    font-size: 0.73rem;
  }

  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1031;
    pointer-events: all;
  }

  .app-footer__inner {
    max-width: 100%;
    border-radius: 12px 12px 0 0;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .btn-footer-back,
  .btn-footer-finish {
    padding: 0.6rem 0.9rem;
    font-size: 0.86rem;
  }
}
</style>
